import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  FileOutput, Loader2, X, Download, RefreshCw, AlertTriangle, Palette, GitCompare,
  Archive, Copy, Eye, TrendingUp, TrendingDown, Minus,
} from "lucide-react";

const OPTION_LABELS = {
  include_executive_summary: "AI Executive Summary", include_medical_timeline: "Medical Timeline",
  include_bills: "Bills", include_provider_directory: "Provider Directory",
  include_appointment_history: "Appointment History", include_risk_analysis: "AI Risk Analysis",
  include_missing_records: "Missing Records", include_future_treatment: "Future Treatment",
  include_value_indicators: "AI Value Indicators", include_attorney_notes: "Internal Attorney Notes",
  exclude_private_notes: "Exclude Private Notes", exclude_draft_records: "Exclude Draft Records",
};
const REVIEW_LABELS = {
  draft: "Draft", ready_for_review: "Ready for Review", attorney_reviewed: "Attorney Reviewed",
  approved: "Approved", shared: "Shared", filed: "Filed", archived: "Archived",
};
const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm";

const BrandingEditor = ({ onClose }) => {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/attorney/firm-branding").then(({ data }) => setB(data)).catch(() => {}); }, []);
  if (!b) return <Loader2 className="w-5 h-5 animate-spin text-cobalt mx-auto my-6" />;
  const save = async () => {
    try { await api.put("/attorney/firm-branding", b); toast.success("Branding saved — all exports now use it"); onClose(); }
    catch { toast.error("Save failed"); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/attorney/firm-branding/logo", fd); toast.success("Logo uploaded"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
  };
  const F = ({ k, placeholder }) => (
    <input value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })} data-testid={`branding-${k}`} className={inputCls} placeholder={placeholder} />
  );
  return (
    <div className="space-y-2.5 mt-3" data-testid="branding-editor">
      <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">Logo:
        <input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="branding-logo" className="text-xs font-normal" />
        {b.logo_path && <span className="text-emerald-600">✓ on file</span>}
      </label>
      <div className="grid sm:grid-cols-2 gap-2">
        <F k="firm_name" placeholder="Firm name" />
        <F k="attorney_display_name" placeholder="Attorney name (as shown on exports)" />
        <F k="address" placeholder="Firm address" />
        <F k="offices" placeholder="Office locations" />
        <F k="phone" placeholder="Phone" />
        <F k="email" placeholder="Email" />
        <F k="website" placeholder="Website" />
        <F k="social_links" placeholder="Social links (optional)" />
      </div>
      <F k="letterhead" placeholder="Letterhead line (printed on every page)" />
      <F k="contact_info" placeholder="Contact information line" />
      <textarea value={b.signature_block || ""} onChange={(e) => setB({ ...b, signature_block: e.target.value })} data-testid="branding-signature" className={`${inputCls} h-16`} placeholder="Attorney signature block" />
      <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">Firm color:
        <input type="color" value={b.color || "#0b1f3a"} onChange={(e) => setB({ ...b, color: e.target.value })} data-testid="branding-color" />
      </label>
      <button onClick={save} data-testid="branding-save" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold">Save branding</button>
    </div>
  );
};

const MetricDelta = ({ m }) => (
  <div className="flex items-center justify-between gap-2 py-1 border-b border-slate-50 last:border-0" data-testid={`metric-delta-${m.key}`}>
    <span className="text-slate-500">{m.label}</span>
    <span className="flex items-center gap-1.5 font-semibold text-navy">
      {m.previous}{m.is_percent ? "%" : ""} → {m.current}{m.is_percent ? "%" : ""}
      {m.direction === "improved" && <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />}
      {m.direction === "reduced" && <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
      {m.direction === "no_change" && <Minus className="w-3.5 h-3.5 text-slate-300" />}
    </span>
  </div>
);

const ComparePanel = ({ data, title, onClose }) => (
  <div className="mt-4 rounded-2xl border border-slate-200 p-4 text-xs" data-testid="export-compare-result">
    <div className="flex items-center justify-between">
      <span className="font-bold text-navy">{title}</span>
      <button onClick={onClose} data-testid="compare-close"><X className="w-4 h-4 text-slate-400" /></button>
    </div>
    {(data.changes_summary || []).length > 0 && (
      <div className="mt-2 rounded-xl bg-emerald-50/60 p-3 space-y-1" data-testid="changes-summary">
        <div className="font-semibold text-emerald-700 uppercase text-[10px]">What's changed</div>
        {data.changes_summary.map((line, i) => <div key={i} className="text-emerald-800">✓ {line}</div>)}
      </div>
    )}
    <div className="mt-3 grid sm:grid-cols-2 gap-3">
      <div>
        {Object.entries(data.new || {}).map(([k, v]) => (
          <div key={k} className="mb-2" data-testid={`compare-new-${k}`}>
            <div className="font-semibold text-navy">NEW {v.label}</div>
            {v.items.slice(0, 8).map((it, i) => <div key={i} className="text-slate-500 truncate">+ {it}</div>)}
          </div>
        ))}
        {Object.entries(data.updated || {}).map(([k, v]) => (
          <div key={k} className="mb-2" data-testid={`compare-updated-${k}`}>
            <div className="font-semibold text-amber-600">UPDATED {v.label}</div>
            {v.added.slice(0, 6).map((it, i) => <div key={i} className="text-slate-500 truncate">+ {it}</div>)}
            {v.removed.slice(0, 4).map((it, i) => <div key={i} className="text-slate-400 line-through truncate">− {it}</div>)}
          </div>
        ))}
        {Object.keys(data.new || {}).length === 0 && Object.keys(data.updated || {}).length === 0 && (
          <p className="text-slate-400" data-testid="compare-no-changes">No content changes detected.</p>
        )}
        {(data.added_records || []).length > 0 && (
          <div className="mt-2" data-testid="compare-added-records">
            <div className="font-semibold text-navy">New source records</div>
            {data.added_records.map((r, i) => <div key={i} className="text-slate-500 truncate">+ {r}</div>)}
          </div>
        )}
      </div>
      <div>
        <div className="font-semibold text-navy mb-1">Settlement Readiness Delta</div>
        {(data.metrics_delta || []).map((m) => <MetricDelta key={m.key} m={m} />)}
      </div>
    </div>
  </div>
);

export const ExportCenter = ({ caseId, caseNumber, onClose }) => {
  const [types, setTypes] = useState(null);
  const [history, setHistory] = useState(null);
  const [etype, setEtype] = useState("demand_package_medical_section");
  const [fmt, setFmt] = useState("pdf");
  const [opts, setOpts] = useState({});
  const [showBranding, setShowBranding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [compare, setCompare] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/litigation/exports?case_id=${caseId}`).then(({ data }) => {
      setHistory(data);
      if ((data.exports || []).some((e) => e.status === "pending")) {
        clearTimeout(pollRef.current);
        pollRef.current = setTimeout(load, 2500);
      }
    }).catch(() => setHistory({ exports: [] }));
  }, [caseId]);
  useEffect(() => {
    api.get("/litigation/export-types").then(({ data }) => setTypes(data)).catch(() => {});
    load();
    return () => clearTimeout(pollRef.current);
  }, [load]);

  const generate = async (overrides = {}) => {
    try {
      const { data } = await api.post("/litigation/exports", { case_id: caseId, export_type: etype, format: fmt, options: opts, ...overrides });
      if (data.cached) toast.info(data.message);
      else toast.success(`Export v${data.version} started — est. ${data.estimated_seconds}s`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };
  const regenerate = async (ex) => {
    try {
      const { data } = await api.post(`/litigation/exports/${ex.export_id}/duplicate`, {});
      toast.success(`Regenerating as v${data.version}…`); load();
    } catch { toast.error("Regenerate failed"); }
  };
  const download = async (ex) => {
    try {
      const res = await api.get(`/litigation/exports/${ex.export_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${ex.export_type}_v${ex.version || 1}_${ex.case_number}.${ex.file?.ext || "pdf"}`; a.click();
      URL.revokeObjectURL(url); load();
    } catch { toast.error("Download failed"); }
  };
  const doCompare = async (ex) => {
    const other = (history.exports || []).find((o) => o.export_id !== ex.export_id && o.status === "ready" && o.export_type === ex.export_type)
      || (history.exports || []).find((o) => o.export_id !== ex.export_id && o.status === "ready");
    if (!other) { toast.error("No other completed export to compare with"); return; }
    try {
      const { data } = await api.get(`/litigation/exports/${ex.export_id}/compare/${other.export_id}`);
      setCompare({ ...data, _title: `Changes since Version ${data.older?.version} (v${data.older?.version} → v${data.newer?.version})` });
    } catch { toast.error("Compare failed"); }
  };
  const whatsNew = async (ex) => {
    try {
      const { data } = await api.get(`/litigation/exports/${ex.export_id}/whats-new`);
      setCompare({ ...data, _title: `New since v${ex.version || 1} was generated` });
    } catch { toast.error("Could not load new records"); }
  };
  const ignoreStale = async (ex) => {
    try { await api.post(`/litigation/exports/${ex.export_id}/ack-stale`); toast.success("Dismissed for this version"); load(); }
    catch { toast.error("Failed"); }
  };
  const setReview = async (ex, status) => {
    try { await api.post(`/litigation/exports/${ex.export_id}/review-status`, { status }); toast.success(`Marked ${REVIEW_LABELS[status]}`); load(); }
    catch { toast.error("Failed to update status"); }
  };

  const rows = (history?.exports || []).filter((e) => showArchived || e.review_status !== "archived");

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="export-center">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-navy flex items-center gap-2"><FileOutput className="w-5 h-5 text-cobalt" /> Litigation Export Center — {caseNumber}</h3>
          <button onClick={onClose} data-testid="export-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {types && <p className="mt-2 text-[11px] font-semibold text-indigo-700 bg-indigo-100/70 rounded-xl px-3 py-2" data-testid="export-disclaimer">{types.disclaimer}</p>}

        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          <select value={etype} onChange={(e) => setEtype(e.target.value)} data-testid="export-type" className={`${inputCls} sm:col-span-2`}>
            {(types?.types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="export-format" className={inputCls}>
            <option value="pdf">PDF (printable)</option><option value="docx">Microsoft Word (.docx)</option>
          </select>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {Object.entries(OPTION_LABELS).map(([k, label]) => (
            <label key={k} className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <input type="checkbox" checked={!!opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })}
                data-testid={`export-opt-${k}`} className="accent-cobalt" /> {label}
            </label>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <button onClick={() => generate()} data-testid="export-generate" className="px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold">
            {etype === "demand_package_medical_section" ? "Generate Demand Package" : etype === "litigation_medical_packet" ? "Generate Litigation Binder" : "Generate Export"}
          </button>
          <button onClick={() => setShowBranding(!showBranding)} data-testid="export-branding" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs">
            <Palette className="w-3.5 h-3.5" /> Firm branding
          </button>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500 ml-auto">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="export-show-archived" className="accent-cobalt" /> Show archived
          </label>
        </div>
        {showBranding && <BrandingEditor onClose={() => setShowBranding(false)} />}

        <div className="mt-5">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Export history (permanent — never deleted)</div>
          {history === null ? <Loader2 className="w-5 h-5 animate-spin text-cobalt" /> :
            rows.length === 0 ? <p className="text-sm text-slate-400" data-testid="export-history-empty">No exports yet.</p> : (
            <div className="divide-y divide-slate-50">
              {rows.map((ex) => (
                <div key={ex.export_id} className="py-2.5" data-testid={`export-row-${ex.export_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-semibold text-navy">{ex.export_type.replace(/_/g, " ")}</span>
                      <span className="ml-2 px-1.5 py-0.5 rounded-md bg-slate-100 text-[10px] font-bold text-slate-600" data-testid={`export-version-${ex.export_id}`}>v{ex.version || 1}</span>
                      {ex.is_latest && ex.status === "ready" && !ex.is_stale && <span className="ml-1 px-1.5 py-0.5 rounded-md bg-emerald-100 text-[10px] font-bold text-emerald-700">LATEST</span>}
                      {ex.is_stale && <span className="ml-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-[10px] font-bold text-amber-700" data-testid={`export-outdated-${ex.export_id}`}>OUTDATED</span>}
                      {ex.review_status === "archived" && <span className="ml-1 px-1.5 py-0.5 rounded-md bg-slate-200 text-[10px] font-bold text-slate-500">ARCHIVED</span>}
                      <div className="text-[11px] text-slate-400">
                        {(ex.file?.ext || ex.format).toUpperCase()} · {new Date(ex.created_at).toLocaleString()} · by {ex.generated_by}
                        · {(ex.downloads || []).length} download(s) · {(ex.views || []).length} view(s)
                        {ex.reviewed_by && ex.review_status !== "draft" && <> · {REVIEW_LABELS[ex.review_status]} by {ex.reviewed_by} {new Date(ex.reviewed_at).toLocaleString()}</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {ex.status === "pending" && (
                        <span className="flex items-center gap-2 text-[11px] text-cobalt font-semibold" data-testid={`export-progress-${ex.export_id}`}>
                          <span className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-cobalt transition-all" style={{ width: `${ex.progress || 0}%` }} /></span>
                          {ex.progress || 0}% · ~{ex.estimated_seconds || 10}s
                        </span>
                      )}
                      {ex.status === "failed" && <span className="text-[11px] text-red-500">failed: {ex.error}</span>}
                      {ex.status === "ready" && <>
                        <select value={ex.review_status || "draft"} onChange={(e) => setReview(ex, e.target.value)} data-testid={`export-review-${ex.export_id}`}
                          className="rounded-full border border-slate-200 px-2 py-1 text-[10px] text-slate-600">
                          {Object.entries(REVIEW_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <button onClick={() => download(ex)} data-testid={`export-download-${ex.export_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold"><Download className="w-3 h-3" /> Download</button>
                        <button onClick={() => doCompare(ex)} title="Compare versions" data-testid={`export-compare-${ex.export_id}`} className="p-1.5 rounded-full border border-slate-200 text-slate-500"><GitCompare className="w-3.5 h-3.5" /></button>
                        <button onClick={() => regenerate(ex)} title="Duplicate / regenerate" data-testid={`export-duplicate-${ex.export_id}`} className="p-1.5 rounded-full border border-slate-200 text-slate-500"><Copy className="w-3.5 h-3.5" /></button>
                        {ex.review_status !== "archived" && <button onClick={() => setReview(ex, "archived")} title="Archive" data-testid={`export-archive-${ex.export_id}`} className="p-1.5 rounded-full border border-slate-200 text-slate-400"><Archive className="w-3.5 h-3.5" /></button>}
                      </>}
                    </div>
                  </div>
                  {ex.is_stale && !ex.stale_acknowledged && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-amber-600 bg-amber-50 rounded-xl px-3 py-1.5" data-testid={`export-stale-${ex.export_id}`}>
                      <AlertTriangle className="w-3 h-3" /> {history.stale_notice}
                      <button onClick={() => doCompare(ex)} data-testid={`export-stale-compare-${ex.export_id}`} className="flex items-center gap-1 underline underline-offset-2"><GitCompare className="w-3 h-3" /> Compare Versions</button>
                      <button onClick={() => regenerate(ex)} data-testid={`export-stale-regen-${ex.export_id}`} className="flex items-center gap-1 underline underline-offset-2"><RefreshCw className="w-3 h-3" /> Regenerate Export</button>
                      <button onClick={() => whatsNew(ex)} data-testid={`export-stale-new-${ex.export_id}`} className="flex items-center gap-1 underline underline-offset-2"><Eye className="w-3 h-3" /> View New Records</button>
                      <button onClick={() => ignoreStale(ex)} data-testid={`export-stale-ignore-${ex.export_id}`} className="text-slate-400 underline underline-offset-2">Ignore</button>
                    </div>
                  )}
                  {ex.sections_included && <div className="text-[10px] text-slate-400 mt-1">Sections: {ex.sections_included.join(" · ")} · Engine {ex.engine_version || "LEX"} · AI {ex.ai_engine_version || "gpt-4o"} · {ex.medical_records_version ?? 0} record(s)</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {compare && <ComparePanel data={compare} title={compare._title || "Version comparison"} onClose={() => setCompare(null)} />}
      </div>
    </div>
  );
};

export default ExportCenter;
